import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { principles, site, stats, testimonials } from '@/mocks/guagua';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const values = [
  {
    icon: 'ri-emotion-happy-line',
    title: '轻松但不轻浮',
    desc: '话题可以聊得轻松，但每个说法背后都得站得住脚。标题可以有趣，事实不能打折。',
  },
  {
    icon: 'ri-focus-3-line',
    title: '只做一件事',
    desc: '我们不做大而全，只把"今天发生了什么"这件事讲清楚，让你用最少的时间跟上节奏。',
  },
  {
    icon: 'ri-lock-2-line',
    title: '给隐私留白',
    desc: '公众人物的公开动态可以讨论，但与话题无关的普通人，我们尽量不让他们出现在标题里。',
  },
];

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于瓜哥',
    inLanguage: 'zh-CN',
    description: site.intro,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dfhov.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于瓜哥', item: 'https://dfhov.cn/about' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    email: site.email,
    url: 'https://dfhov.cn/',
    description: site.intro,
  },
];

export default function About() {
  useDocumentMeta(
    '关于瓜哥 - 网站定位与内容原则｜瓜哥吃瓜',
    '了解瓜哥吃瓜的网站定位、发展理念与内容原则：快但不抢跑，有分寸地围观，每条内容标注来源与时间，并持续回访更新。',
  );

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd data={aboutSchema} />
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="relative w-full overflow-hidden bg-foreground-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20desk%20scene%20illustration%20with%20notebooks%2C%20coffee%20cup%20and%20soft%20morning%20light%2C%20coral%20and%20cream%20gradient%20tones%2C%20stylized%20flat%20shapes%2C%20calm%20editorial%20aesthetic%2C%20lots%20of%20empty%20space%2C%20no%20text&width=1600&height=700&seq=guagua-about-2026&orientation=landscape"
              alt="瓜哥吃瓜内容编辑工作场景氛围图"
              title="关于瓜哥吃瓜"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/90 via-foreground-950/75 to-foreground-950/90"></div>
          <div className="relative w-full px-4 md:px-6 pt-24 md:pt-28 pb-14 md:pb-16">
            <div className="max-w-6xl mx-auto">
              <div className="[&_a]:text-background-300 [&_span]:text-background-400">
                <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于瓜哥' }]} />
              </div>
              <h1 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50">
                关于瓜哥
              </h1>
              <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-2xl">
                {site.intro}
              </p>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16" aria-labelledby="about-position">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-3">
                <span className="w-6 h-px bg-primary-400"></span>
                Positioning
              </span>
              <h2 id="about-position" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                一个把热点收拢到一处的门户
              </h2>
              <div className="mt-5 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  信息的获取方式变了，但"今天到底发生了什么"这个问题一直没变。不同的是，现在要在十几个平台之间来回切换，才能拼出一张完整的图。
                </p>
                <p>
                  {site.name}想解决的正是这件事：把正在被讨论的话题按栏目整理好，配上时间和来源，让你不用到处翻，一眼就能看到全貌。
                </p>
                <p>
                  面向的是普通读者——通勤路上刷几分钟的打工人，想快速了解行业动态的内容从业者，以及那些对世界还保持着好奇心的普通人。
                </p>
                <p>
                  我们不生产话题，只是话题的整理者。所有内容都基于公开渠道，对于尚未有官方说明的传闻，我们会明确标注，而不是当作事实发布。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 content-start">
              {stats.map((item) => (
                <div key={item.label} className="p-5 rounded-lg bg-background-100 border border-background-200">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{item.value}</div>
                  <div className="mt-1 text-xs md:text-sm text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100" aria-labelledby="about-values">
          <div className="max-w-6xl mx-auto">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-3">
              <span className="w-6 h-px bg-primary-400"></span>
              Values
            </span>
            <h2 id="about-values" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              我们相信的三件事
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {values.map((item) => (
                <div key={item.title} className="p-6 rounded-lg bg-background-50 border border-background-200">
                  <span className="flex items-center justify-center w-12 h-12 rounded-md bg-accent-500 text-foreground-950 text-xl">
                    <i className={item.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="principles" className="px-4 md:px-6 py-12 md:py-16 scroll-mt-24" aria-labelledby="about-principles">
          <div className="max-w-6xl mx-auto">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-3">
              <span className="w-6 h-px bg-primary-400"></span>
              Principles
            </span>
            <h2 id="about-principles" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              内容原则
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              这四条是编辑流程里绕不开的关卡，每一篇内容发布前都要过一遍。
            </p>
            <ol className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {principles.map((item, index) => (
                <li
                  key={item.title}
                  className="flex gap-4 p-6 rounded-lg bg-background-50 border border-background-200"
                >
                  <span className="font-heading text-3xl font-bold text-background-300 leading-none shrink-0">
                    0{index + 1}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                      <i className={`${item.icon} text-secondary-500`} aria-hidden="true"></i>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100" aria-labelledby="about-voices">
          <div className="max-w-6xl mx-auto">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-3">
              <span className="w-6 h-px bg-primary-400"></span>
              Voices
            </span>
            <h2 id="about-voices" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              读者怎么说
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((item) => (
                <blockquote key={item.name} className="flex flex-col p-6 rounded-lg bg-background-50 border border-background-200">
                  <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true"></i>
                  <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed flex-1">{item.text}</p>
                  <footer className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-500 text-background-50 font-medium">
                      {item.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.tag}</span>
                    </span>
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16" aria-labelledby="about-contact">
          <div className="max-w-6xl mx-auto rounded-lg bg-foreground-950 p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 id="about-contact" className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  联系我们
                </h2>
                <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed">
                  有线索、想推荐话题，或是对内容有任何建议，都欢迎写邮件给我们。我们会在两个工作日内回复。
                </p>
                <div className="mt-6 space-y-3 text-sm">
                  <a
                    href={`mailto:${site.email}`}
                    rel="nofollow"
                    className="flex items-center gap-3 text-background-100 hover:text-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center justify-center w-9 h-9 rounded-md bg-background-900">
                      <i className="ri-mail-line" aria-hidden="true"></i>
                    </span>
                    {site.email}
                  </a>
                  <p className="flex items-center gap-3 text-background-300">
                    <span className="flex items-center justify-center w-9 h-9 rounded-md bg-background-900">
                      <i className="ri-time-line" aria-hidden="true"></i>
                    </span>
                    内容全天滚动更新，回复时间为工作日 10:00 - 19:00
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
                <a
                  href={`mailto:${site.email}`}
                  rel="nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-mail-send-line" aria-hidden="true"></i>
                  写邮件给瓜哥
                </a>
                <Link
                  to="/news"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
                >
                  去看新瓜速递
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}